import { useEffect, useState } from 'react';
import { bg, line, tx, font, accent, space, radius } from '../theme';
import { ACHIEVEMENTS, CATEGORIES, TOTAL_ACHIEVEMENTS, CategoryIcon } from './achievementsData';
import { listUnlockedAchievements } from './accountApi';

function Screen({ children }) {
  return (
    <div style={{
      minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: tx.muted, fontFamily: font.display, fontSize: 12,
      letterSpacing: '0.2em', textTransform: 'uppercase',
    }}>{children}</div>
  );
}

const eyebrow = {
  fontFamily: font.display, fontSize: 11, fontWeight: 700,
  letterSpacing: '0.24em', textTransform: 'uppercase', color: accent.lime,
};

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function Card({ a, unlockedAt }) {
  const cat = CATEGORIES[a.cat] || {};
  const col = cat.accent || accent.lime;
  const unlocked = Boolean(unlockedAt);
  return (
    <div style={{
      position: 'relative', overflow: 'hidden',
      borderRadius: radius.lg, padding: space.s4,
      display: 'flex', gap: space.s3, alignItems: 'flex-start',
      background: unlocked
        ? `linear-gradient(140deg, ${col}1F, ${bg.surface} 70%)`
        : bg.surface,
      border: `1px solid ${unlocked ? col : line.DEFAULT}`,
      opacity: unlocked ? 1 : 0.62,
    }}>
      <div style={{
        flexShrink: 0, width: 46, height: 46, borderRadius: radius.md,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: unlocked ? `${col}22` : bg.raised,
        border: `1px solid ${unlocked ? col : line.strong}`,
        color: unlocked ? col : tx.faint,
      }}>
        {unlocked
          ? <CategoryIcon cat={a.cat} size={24} />
          : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="5" y="10.5" width="14" height="9.5" rx="2" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8 10.5V8a4 4 0 0 1 8 0v2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
            </svg>
          )}
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{
          fontFamily: font.display, fontWeight: 700, fontSize: 15.5,
          color: unlocked ? tx.primary : tx.secondary, lineHeight: 1.15,
        }}>{a.title}</div>
        <p style={{
          color: unlocked ? tx.secondary : tx.muted, fontSize: 13, margin: `4px 0 0`, lineHeight: 1.4,
        }}>{a.desc}</p>
        {unlocked && (
          <div style={{
            marginTop: space.s2, fontFamily: font.data, fontSize: 11, color: col,
            letterSpacing: '0.04em',
          }}>
            Unlocked {fmtDate(unlockedAt)}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Achievements() {
  const [unlocked, setUnlocked] = useState({});
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await listUnlockedAchievements();
      if (!alive) return;
      // Definitions are client-side, so even if unlock state can't load we still
      // show the full grid (everything locked) rather than blanking the page.
      const map = {};
      if (!error) (data || []).forEach((r) => { map[r.key] = r.unlocked_at; });
      setUnlocked(map);
      setStatus('ready');
    })();
    return () => { alive = false; };
  }, []);

  if (status === 'loading') return <Screen>Loading achievements</Screen>;

  const earned = ACHIEVEMENTS.filter((a) => unlocked[a.key]).length;
  const pct = Math.round((earned / TOTAL_ACHIEVEMENTS) * 100);
  const cats = Object.keys(CATEGORIES);

  return (
    <div style={{ maxWidth: 1040, padding: space.s6 }}>
      <div style={{ marginBottom: space.s6 }}>
        <div style={eyebrow}>Career</div>
        <h1 style={{
          fontFamily: font.display, fontWeight: 700, margin: `${space.s2}px 0 0`,
          fontSize: 'clamp(30px, 4vw, 42px)', letterSpacing: '-0.01em',
        }}>Achievements.</h1>
        <p style={{ color: tx.secondary, marginTop: space.s3, fontSize: 15, maxWidth: '54ch' }}>
          Milestones you earn as you build your career. They carry across every save.
        </p>

        <div style={{ marginTop: space.s5, maxWidth: 420 }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            fontFamily: font.display, marginBottom: space.s2,
          }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: tx.primary }}>
              {earned} <span style={{ color: tx.muted }}>/ {TOTAL_ACHIEVEMENTS} unlocked</span>
            </span>
            <span style={{ fontFamily: font.data, fontSize: 12, color: accent.lime }}>{pct}%</span>
          </div>
          <div style={{ height: 8, borderRadius: 99, background: bg.raised, overflow: 'hidden', border: `1px solid ${line.DEFAULT}` }}>
            <div style={{
              width: `${pct}%`, height: '100%', borderRadius: 99,
              background: `linear-gradient(90deg, ${accent.lime}, ${accent.cyan})`,
              transition: 'width .4s ease',
            }} />
          </div>
        </div>
      </div>

      {cats.map((catKey) => {
        const group = ACHIEVEMENTS.filter((a) => a.cat === catKey);
        if (group.length === 0) return null;
        const cat = CATEGORIES[catKey];
        const got = group.filter((a) => unlocked[a.key]).length;
        return (
          <section key={catKey} style={{ marginBottom: space.s6 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: space.s3, marginBottom: space.s4 }}>
              <span style={{ color: cat.accent, display: 'flex' }}><CategoryIcon cat={catKey} size={18} /></span>
              <span style={{
                fontFamily: font.display, fontSize: 12.5, fontWeight: 700,
                letterSpacing: '0.14em', textTransform: 'uppercase', color: tx.secondary,
              }}>{cat.label}</span>
              <span style={{ flex: 1, height: 1, background: line.DEFAULT }} />
              <span style={{ fontFamily: font.data, fontSize: 11, color: tx.faint }}>{got} / {group.length}</span>
            </div>
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: space.s3,
            }}>
              {group.map((a) => (
                <Card key={a.key} a={a} unlockedAt={unlocked[a.key]} />
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}
